<script lang="ts">
  // Loan amount range editor per INTERFACE-SPECIFICATION.md §6
  // (Detail / Edit Mode) and §4.6 (the matrix view that surfaces
  // min / max / increment as the product-level loan amount range).
  //
  // The maintenance interface previously rendered min, max, and
  // increment as read-only fields. PR 4.1 adds inline editors gated
  // on proposer:amounts so the operator can stage an amount-range
  // change, save it through the draft branch, and route to the
  // submission form. The value type is 'amounts'; reviewer:amounts
  // (Dan in production) is the role gated on the resulting proposal.
  //
  // Validation is local and conservative: positive integers, max
  // strictly greater than min, increment greater than zero. The
  // server-side schema validator catches additional structural
  // failures at validate / submit time.

  import {
    stageAmountEdit,
    unstageAmountEdit,
    stagedEditsState
  } from '../lib/staged-edits.svelte';
  import { navigate } from '../lib/router.svelte';
  import { formatAmount } from '../lib/formatters';
  import type { ProductCatalogueFile, ProposeResponse } from '../lib/types';
  import type { AmountField } from '../lib/staged-edits';
  import SubmitForReview from './SubmitForReview.svelte';
  import ValueTypeLockBanner from './ValueTypeLockBanner.svelte';

  interface Props {
    catalogue: ProductCatalogueFile;
  }

  const { catalogue }: Props = $props();

  const baseRange = $derived(catalogue.loan_amount_range);

  // Local input buffer keyed by field. Strings so the user can type
  // mid-edit without immediate coercion; a commit step (blur / Enter)
  // parses and either stages or surfaces a validation message.
  const inputBuffer = $state<Record<AmountField, string>>({
    min: '',
    max: '',
    increment: ''
  });
  const fieldError = $state<Record<AmountField, string | null>>({
    min: null,
    max: null,
    increment: null
  });

  // Lock when the store holds edits of a different value type. The
  // banner that appears in place of the editor inputs gives a clean
  // way out (Discard) and a meaningful explanation. Reactive on the
  // store, so a discard / submit anywhere else in the SPA unlocks
  // automatically — no stale-state-after-revert trap.
  const locked = $derived(
    stagedEditsState.valueType !== null && stagedEditsState.valueType !== 'amounts'
  );

  // Initialise the buffers from the current staged value (if any) or
  // the catalogue baseline. Re-runs when the catalogue or staged
  // amount edits change so the buffer reflects the latest state.
  $effect(() => {
    for (const field of ['min', 'max', 'increment'] as const) {
      const staged = stagedEditsState.amount_edits[field]?.staged;
      const current = staged ?? baseRange[field];
      inputBuffer[field] = current == null ? '' : String(current);
    }
  });

  let submitOpen = $state<boolean>(false);

  function openSubmit() { submitOpen = true; }
  function closeSubmit() { submitOpen = false; }
  function onProposalCreated(response: ProposeResponse) {
    submitOpen = false;
    navigate(`/changes/pending-review/${response.pr_number}`);
  }

  function parseInteger(raw: string): number | null {
    if (!/^\d+$/.test(raw.trim())) return null;
    const n = Number(raw.trim());
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  function validatePending(field: AmountField, value: number): string | null {
    const otherStaged = (other: AmountField): number => {
      const staged = stagedEditsState.amount_edits[other]?.staged;
      return staged ?? baseRange[other];
    };
    if (field === 'min') {
      if (value < 0) return 'Minimum cannot be negative.';
      if (value >= otherStaged('max')) return 'Minimum must be strictly less than maximum.';
      return null;
    }
    if (field === 'max') {
      if (value <= otherStaged('min')) return 'Maximum must be strictly greater than minimum.';
      return null;
    }
    if (field === 'increment') {
      if (value <= 0) return 'Increment must be greater than zero.';
      return null;
    }
    return null;
  }

  function commitEdit(field: AmountField) {
    const raw = inputBuffer[field];
    const parsed = parseInteger(raw);
    if (parsed === null) {
      fieldError[field] = 'Enter a whole-dollar amount (positive integer).';
      return;
    }
    if (parsed === baseRange[field]) {
      fieldError[field] = null;
      unstageAmountEdit(field);
      return;
    }
    const validationMessage = validatePending(field, parsed);
    if (validationMessage) {
      fieldError[field] = validationMessage;
      return;
    }
    fieldError[field] = null;
    stageAmountEdit(field, baseRange[field], parsed);
  }

  function handleInputBlur(field: AmountField) {
    commitEdit(field);
  }

  function handleInputKeyDown(field: AmountField, event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitEdit(field);
      (event.target as HTMLInputElement).blur();
    }
  }

  function revertField(field: AmountField) {
    unstageAmountEdit(field);
    inputBuffer[field] = String(baseRange[field]);
    fieldError[field] = null;
  }

  const stagedCount = $derived(Object.keys(stagedEditsState.amount_edits).length);
</script>

<section class="amounts-editor">
  {#if locked && stagedEditsState.valueType}
    <ValueTypeLockBanner
      currentValueType={stagedEditsState.valueType}
      attemptedValueType="amounts"
    />
  {/if}
  <table class="amounts-table" class:is-locked={locked}>
    <thead>
      <tr>
        <th scope="col">Field</th>
        <th scope="col" class="num">Current</th>
        <th scope="col" class="num">Staged</th>
        <th scope="col" class="num">Δ</th>
        <th scope="col" class="actions">Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each ['min', 'max', 'increment'] as field (field)}
        {@const staged = stagedEditsState.amount_edits[field as AmountField]?.staged ?? null}
        {@const base = baseRange[field as 'min' | 'max' | 'increment']}
        {@const delta = staged !== null ? staged - base : null}
        <tr class:is-staged={staged !== null}>
          <td><strong>{field === 'min' ? 'Minimum' : field === 'max' ? 'Maximum' : 'Increment'}</strong></td>
          <td class="num">${formatAmount(base)}</td>
          <td class="num">
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              aria-label="{field} amount"
              disabled={locked}
              bind:value={inputBuffer[field as AmountField]}
              onblur={() => handleInputBlur(field as AmountField)}
              onkeydown={(e) => handleInputKeyDown(field as AmountField, e)}
            />
            {#if fieldError[field as AmountField]}
              <span class="field-error">{fieldError[field as AmountField]}</span>
            {/if}
          </td>
          <td class="num">{delta === null ? '—' : (delta > 0 ? '+' : '') + formatAmount(delta)}</td>
          <td class="actions">
            {#if staged !== null}
              <button class="btn-link" type="button" disabled={locked} onclick={() => revertField(field as AmountField)}>Revert</button>
            {/if}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>

  {#if !locked && stagedCount > 0}
    <div class="amounts-editor__actions">
      <span class="muted">{stagedCount} amount-range edit{stagedCount === 1 ? '' : 's'} staged</span>
      <button class="btn btn-primary" type="button" onclick={openSubmit}>Submit for review →</button>
    </div>
  {/if}
</section>

{#if submitOpen}
  <SubmitForReview
    {catalogue}
    valueType="amounts"
    onClose={closeSubmit}
    onSubmitted={onProposalCreated}
  />
{/if}

<style>
  .amounts-editor {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .amounts-table {
    width: 100%;
    border-collapse: collapse;
  }
  /* When the ValueTypeLockBanner is rendered, mute the rest of the
     editor surface so the operator's eye is drawn to the banner's
     Discard affordance rather than fiddling with disabled inputs. */
  .amounts-table.is-locked {
    opacity: 0.55;
  }
  .amounts-table th,
  .amounts-table td {
    padding: var(--space-2) var(--space-3);
    border-bottom: 1px solid var(--surface-line-soft);
    text-align: left;
  }
  .amounts-table th.num,
  .amounts-table td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-family: var(--font-mono);
  }
  .amounts-table th.actions,
  .amounts-table td.actions {
    text-align: right;
  }
  .amounts-table input {
    width: 8rem;
    padding: var(--space-1) var(--space-2);
    text-align: right;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }
  .amounts-table tr.is-staged {
    background: var(--surface-highlight, transparent);
  }
  .field-error {
    display: block;
    color: var(--status-fail, #b00020);
    font-size: var(--type-xs);
    margin-top: var(--space-1);
  }
  .amounts-editor__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: var(--space-2);
  }
</style>
